import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { BehaviorSubject, Observable, Subject, takeUntil, tap } from 'rxjs';
import { BottomSheetService } from '@app/bottom-sheet.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MonthPipe } from '@app/when/month.pipe';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { DayComponent } from '@app/when/day/day.component';
import { MobileDatePipe } from './mobile-date.pipe';
import { AvailableDate } from '@app/services/api/api.service';
import { DayToDatePipe } from '@app/when/day-to-date.pipe';
import { SearchService } from '@app/search.service';
import { DateHelper } from '@app/date-helper';
import { ActivatedRoute, Router } from '@angular/router';
import { ISODatePipe } from '@app/when/iso-date.pipe';

@Component({
  selector: 'app-when-mobile-scroll',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MonthPipe,
    AngularSvgIconModule,
    DayComponent,
    MobileDatePipe,
    DayToDatePipe,
    ISODatePipe,
  ],
  templateUrl: './when-mobile-scroll.component.html',
  styleUrls: ['./when-mobile-scroll.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('toggleInOut', [
      state(
        'in',
        style({
          transform: 'translateX(0)',
        })
      ),
      state(
        'out',
        style({
          transform: 'translateX(100%)',
        })
      ),
      transition('in => out', [animate('0.15s ease-out')]),
      transition('out => in', [animate('0.15s ease-out')]),
    ]),
  ],
  host: { '[@toggleInOut]': '' },
})
export class WhenMobileScrollComponent {
  @Input() public value: any;
  @Input() public activeInput: string | undefined;

  @Input()
  public unavailableDates: AvailableDate[] = [];
  @Input()
  public noAvailability = false;

  public bookingOptions: any = {};

  @ViewChild('checkInOut', { static: false })
  public set checkInOut(el: ElementRef | undefined) {
    if (el) {
      const native = el?.nativeElement;

      this.observer = new IntersectionObserver(
        ([e]) => {
          return e.target.classList.toggle('stuck', e.intersectionRatio < 1);
        },
        { threshold: [1] }
      );

      this.observer.observe(native);
    } else {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = undefined;
      }
    }
  }

  private observer: IntersectionObserver | undefined;

  selection: any;
  days = Array.from({ length: 40 }, (_, i) => i + 1);
  selecting: boolean = false;

  anchorStart?: number;
  anchorEnd?: number;
  focus?: number;

  fromMonth!: number;
  fromYear!: number;

  toMonth!: number;
  toYear!: number;

  toDays!: number;
  fromDays!: number;

  toStartDay!: number;
  fromStartDay!: number;

  private touched = false;

  public edges = [];
  public corners = [];

  public startDayOfMonth: number | undefined;
  public endDayOfMonth: number | undefined;

  public absoluteAnchorStart: number | undefined = 0;
  public absoluteAnchorEnd: number | undefined = 0;
  public monthOffset = 0;

  public checkInOutState = 'in';

  private destroyed = new Subject<void>();

  public startDate: Date | undefined;
  public endDate: Date | undefined;

  /**
   * Must be an ISOString
   */
  public anchorStartDate: string;

  /**
   * Must be an ISOString
   */
  public anchorEndDate: string;

  public noOfGuestsFocused = new BehaviorSubject(false);

  public initialAnchorStart: any;
  public initialAnchorEnd: any;
  public initialAnchorStartDate: any;
  public initialAnchorEndDate: any;

  @Output()
  public close = new EventEmitter<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: string
  ) {}

  ngOnInit() {
    this.fromMonth = new Date().getMonth();
    this.fromYear = new Date().getFullYear();
    this.toYear = this.fromMonth === 11 ? this.fromYear + 1 : this.fromYear;
    this.toMonth = this.fromMonth === 11 ? 0 : this.fromMonth + 1;

    this.fromDays = DateHelper.getDaysInMonth(this.fromMonth, this.fromYear);
    this.toDays = DateHelper.getDaysInMonth(this.toMonth, this.toYear);

    this.fromStartDay = DateHelper.getFirstDayOfMonth(
      this.fromMonth,
      this.fromYear
    );
    this.toStartDay = DateHelper.getFirstDayOfMonth(this.toMonth, this.toYear);

    if (isPlatformBrowser(this.platformId)) {
      this.route.queryParamMap
        .pipe(takeUntil(this.destroyed))
        .subscribe((paramsAsMap: any) => {
          console.log('query params:', paramsAsMap);
          if (paramsAsMap.has('checkInDate')) {
            this.anchorStartDate = paramsAsMap.get('checkInDate');
          }
          // TODO SET INITIAL ANCHORS!!
          if (paramsAsMap.has('checkOutDate')) {
            this.anchorEndDate = paramsAsMap.get('checkOutDate');
          }

          this.bookingOptions = {
            currency: paramsAsMap.get('currency') ?? 'USD',
            checkInDate: paramsAsMap.get('checkInDate'),
            checkOutDate: paramsAsMap.get('checkOutDate'),
            noOfGuests: Number(paramsAsMap.get('noOfGuests') ?? 1),
          };
        });
    }
  }

  public closeBottomSheet() {
    this.close.emit();
  }

  public highlight(i: number) {
    if (this.anchorStart !== undefined) {
      if (i > this.anchorStart) {
        this.focus = i;
      }
    }
  }

  public unhighlight() {
    this.focus = undefined;
  }

  public doUpdate() {
    this.selecting = false;
    this.anchorStartDate = DateHelper.normalizeDateToDateString(this.startDate);
    this.anchorEndDate = DateHelper.normalizeDateToDateString(this.endDate);
    this.bookingOptions.checkInDate = this.startDate;
    this.bookingOptions.checkOutDate = this.endDate;

    this.closeBottomSheet();

    this.anchorStart = undefined;
    this.anchorEnd = undefined;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        checkInDate: this.anchorStartDate,
        checkOutDate: this.anchorEndDate,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  public clicked(day: number, dayOfMonth: number, checkDisabled = false) {
    const today = new Date().getDate();
    if (
      checkDisabled &&
      this.monthOffset === 0 &&
      today + this.fromStartDay > day
    ) {
      return;
    }
    this.selecting = true;
    if (
      this.absoluteAnchorStart &&
      this.absoluteAnchorStart < this.monthOffset
    ) {
      // click on a future month
      this.absoluteAnchorEnd = this.monthOffset;
      this.anchorEnd = day;
      this.endDate = this.getDate(day);
      // this is causing previous day to be highlighted temporarily on click
      this.anchorEndDate = DateHelper.normalizeDateToDateString(this.endDate);
      this.endDayOfMonth = dayOfMonth;
      this.done(day);
      this.checkInOutState = 'in';
    } else if (
      this.absoluteAnchorStart &&
      this.absoluteAnchorStart > this.monthOffset
    ) {
      // click on a previous month
      this.anchorStart = day;
      this.startDate = this.getDate(day);
      // this is causing previous day to be highlighted temporarily on click
      this.anchorStartDate = DateHelper.normalizeDateToDateString(
        this.startDate
      );
      this.absoluteAnchorStart = this.monthOffset;
      this.absoluteAnchorEnd = undefined;
      this.anchorEnd = undefined;
      this.anchorEndDate = undefined;
      this.endDate = undefined;
      this.anchorStartDate = undefined;
      this.startDayOfMonth = dayOfMonth;
      this.endDayOfMonth = undefined;
      this.checkInOutState = 'out';
    } else if (day === this.anchorStart) {
      this.anchorStart = undefined;
      this.anchorStartDate = undefined;
      this.startDate = undefined;
      this.absoluteAnchorStart = undefined;
      this.absoluteAnchorEnd = undefined;
      this.startDayOfMonth = undefined;
      this.endDayOfMonth = undefined;
    } else if (this.anchorStart !== undefined && this.anchorEnd !== undefined) {
      this.anchorStart = day;
      this.startDate = this.getDate(day);
      // this is causing previous day to be highlighted temporarily on click
      this.anchorStartDate = DateHelper.normalizeDateToDateString(
        this.startDate
      );
      this.absoluteAnchorStart = this.monthOffset;
      this.absoluteAnchorEnd = undefined;
      this.anchorEnd = undefined;
      this.anchorEndDate = undefined;
      this.endDate = undefined;
      this.startDayOfMonth = dayOfMonth;
      this.endDayOfMonth = undefined;
      this.checkInOutState = 'out';
    } else if (this.anchorStart !== undefined && day > this.anchorStart) {
      this.absoluteAnchorEnd = this.monthOffset;
      this.anchorEnd = day;
      this.endDate = this.getDate(day);
      // this is causing previous day to be highlighted temporarily on click
      this.anchorEndDate = DateHelper.normalizeDateToDateString(this.endDate);
      this.endDayOfMonth = dayOfMonth;
      this.done(day);
      this.checkInOutState = 'in';
    } else {
      this.anchorStart = day;
      this.startDate = this.getDate(day);
      // this is causing previous day to be highlighted temporarily on click
      this.anchorStartDate = DateHelper.normalizeDateToDateString(
        this.startDate
      );
      this.absoluteAnchorStart = this.monthOffset;
      this.anchorEnd = undefined;
      this.anchorEndDate = undefined;
      this.endDate = undefined;
      this.absoluteAnchorEnd = undefined;
      this.startDayOfMonth = dayOfMonth;
      this.endDayOfMonth = undefined;
      this.checkInOutState = 'out';
    }
  }

  private getDate(selected: number): Date {
    let year;
    let month;
    let day;

    if (selected > 40) {
      year = this.toYear;
      month = this.toMonth;
      day = selected - 40 - this.toStartDay + 1;
    } else {
      year = this.fromYear;
      month = this.fromMonth;
      day = selected - this.fromStartDay + 1;
    }

    return new Date(year, month, day);
  }

  public done(day: number) {
    this.selecting = false;
  }

  reset() {
    this.anchorStart = this.initialAnchorStart;
    this.anchorStartDate = this.initialAnchorStartDate;
    this.anchorEnd = this.initialAnchorEnd;
    this.anchorEndDate = this.initialAnchorEndDate;
    this.absoluteAnchorEnd = undefined;
    this.absoluteAnchorStart = undefined;
    // this.value = undefined;
    this.startDayOfMonth = undefined;
    this.endDayOfMonth = undefined;
    this.selecting = false;
    // this.onChange(this.value);
  }

  public toggleCheckInOut(state: string) {
    this.checkInOutState = state;
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
    // this.closeBottomSheet();
  }
}
